import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { portalApi } from '../../lib/parceiroApi';
import { formatCurrency, formatDate } from '../../lib/utils';
import ParceiroStatusBadge from '../../components/ParceiroStatusBadge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ChevronLeft, Loader2, CheckCircle2, Wallet, Clock, QrCode } from 'lucide-react';
import { toast } from 'sonner';

export default function ParceiroSolicitacaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [os, setOs] = useState(null);
  const [acao, setAcao] = useState(false);
  const [confirmAprovar, setConfirmAprovar] = useState(false);
  const [limiteErro, setLimiteErro] = useState(null);

  const load = useCallback(() => {
    portalApi.getSolicitacao(id).then((r) => setOs(r.data)).catch(() => navigate('/parceiro/solicitacoes'));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const aprovar = async () => {
    setAcao(true);
    try {
      const r = await portalApi.aprovar(id);
      toast.success(`Aprovado! Disponível: ${formatCurrency(r.data.limite.disponivel)}`);
      setConfirmAprovar(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setConfirmAprovar(false);
      if (d?.code === 'limite_insuficiente' || d?.code === 'inadimplente') {
        setLimiteErro(d);
      } else {
        toast.error(typeof d === 'string' ? d : 'Erro ao aprovar');
      }
    } finally {
      setAcao(false);
    }
  };

  const pagarAntecipado = async () => {
    setAcao(true);
    try {
      const r = await portalApi.pagamentoAntecipado(id);
      setLimiteErro(null);
      if (r.data.pix_cobranca_id) {
        toast.success('PIX gerado! Abrindo pagamento...');
        window.open(`/pagar/${r.data.pix_cobranca_id}`, '_blank');
      } else {
        toast.success('Registrado! A Tenance enviará a cobrança antecipada.');
      }
      load();
    } catch (err) {
      toast.error('Erro ao registrar');
    } finally {
      setAcao(false);
    }
  };

  if (!os) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={28} /></div>;

  const end = os.cliente_endereco || {};

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="solicitacao-detalhe">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500"><ChevronLeft size={16} /> Voltar</button>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold text-[#3D4F5F]">#OS-{String(os.numero).padStart(4, '0')}</h1>
        <ParceiroStatusBadge status={os.status} />
      </div>

      {os.aprovacao_parceiro === 'aguardando_aprovacao' && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-semibold text-amber-800">Valor definido pela Tenance: {formatCurrency(os.valor)}</div>
            <p className="text-xs text-amber-700">Aprove para autorizar a execução. O valor será reservado no seu limite de crédito e cobrado em fatura.</p>
            <div className="flex gap-2 flex-wrap">
              <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={acao} onClick={() => setConfirmAprovar(true)} data-testid="btn-aprovar-valor">
                <CheckCircle2 size={16} className="mr-1.5" /> Aprovar ({formatCurrency(os.valor)})
              </Button>
              <Button variant="outline" disabled={acao} onClick={pagarAntecipado} data-testid="btn-pagar-antecipado">
                <Wallet size={16} className="mr-1.5" /> Pagar à vista
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {os.aprovacao_parceiro === 'aguardando_valor' && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 rounded-xl p-3 text-sm flex items-center gap-2">
          <Clock size={16} /> A Tenance está analisando e definirá o orçamento. Você aprovará o valor antes da execução.
        </div>
      )}

      {os.aprovacao_parceiro === 'pagamento_antecipado' && os.pix_cobranca_id && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-4 space-y-2">
            {os.pix_cobranca_status === 'pago' ? (
              <div className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5" data-testid="pix-antecipado-pago">
                <CheckCircle2 size={16} /> Pagamento à vista confirmado
              </div>
            ) : (
              <>
                <div className="text-sm font-semibold text-emerald-800">Pagamento à vista via PIX — {formatCurrency(os.valor)}</div>
                <p className="text-xs text-emerald-700">Pague pelo QR Code ou copia-e-cola. Não consome seu limite de crédito.</p>
                <Button className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => window.open(`/pagar/${os.pix_cobranca_id}`, '_blank')} data-testid="btn-pagar-pix-antecipado">
                  <QrCode size={16} className="mr-1.5" /> Pagar via PIX
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 space-y-2 text-sm">
          <Linha label="Serviço" valor={`${os.servico_categoria ? os.servico_categoria + ' · ' : ''}${os.servico_nome}`} />
          <Linha label="Cliente" valor={`${os.cliente_nome} · ${os.cliente_telefone}`} />
          <Linha label="Imóvel" valor={os.imovel_descricao || '-'} />
          <Linha label="Endereço" valor={`${end.rua || ''}, ${end.numero || ''} ${end.complemento || ''} - ${end.bairro || ''}, ${end.cidade || ''}${end.zona ? ' (' + end.zona + ')' : ''}`} />
          {os.descricao_problema && <Linha label="Descrição" valor={os.descricao_problema} />}
          <Linha label="Prioridade" valor={os.prioridade === 'urgente' ? 'Urgente' : 'Normal'} />
          <Linha label="Data solicitada" valor={`${formatDate(os.data_desejada)}${os.periodo_desejado ? ' · ' + os.periodo_desejado : ''}`} />
          <Linha label="Data confirmada" valor={os.hora_agendamento ? `${formatDate(os.data_agendamento)} às ${os.hora_agendamento}` : formatDate(os.data_agendamento)} />
          {os.colaborador_nome && <Linha label="Profissional" valor={os.colaborador_nome} />}
          {os.valor > 0 && <Linha label="Valor" valor={formatCurrency(os.valor)} destaque />}
          {os.fatura && <Linha label="Fatura" valor={`${os.fatura.codigo} · ${os.fatura.status} · vence ${formatDate(os.fatura.vencimento)}`} />}
          <Linha label="Solicitado por" valor={os.solicitado_por} />
        </CardContent>
      </Card>

      {os.anexos?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-[#3D4F5F] mb-2">Anexos</h2>
            <div className="grid grid-cols-3 gap-2">
              {os.anexos.map((a) => (
                <a key={a.file_id} href={a.url} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-200 overflow-hidden block">
                  {a.content_type?.startsWith('image/')
                    ? <img src={a.url} alt={a.nome} className="h-20 w-full object-cover" />
                    : <div className="h-20 flex items-center justify-center text-[10px] text-gray-500 px-1 text-center">{a.nome}</div>}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <h2 className="text-sm font-semibold text-[#3D4F5F] mb-3">Histórico</h2>
          <div className="space-y-0">
            {(os.eventos || []).map((ev, i) => (
              <div key={i} className="flex gap-3 pb-3 relative">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#2B7A3D] mt-1" />
                  {i < os.eventos.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
                </div>
                <div>
                  <div className="text-sm text-gray-700">{ev.descricao}</div>
                  <div className="text-[11px] text-gray-400">{ev.data ? new Date(ev.data).toLocaleString('pt-BR') : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Confirmação de aprovação */}
      <AlertDialog open={confirmAprovar} onOpenChange={setConfirmAprovar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aprovar {formatCurrency(os.valor)}?</AlertDialogTitle>
            <AlertDialogDescription>O valor será reservado no limite de crédito do parceiro e a Tenance seguirá com a execução.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={aprovar} data-testid="confirmar-aprovacao">Confirmar aprovação</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Limite insuficiente / inadimplente */}
      <AlertDialog open={!!limiteErro} onOpenChange={(v) => !v && setLimiteErro(null)}>
        <AlertDialogContent data-testid="dialog-limite-insuficiente">
          <AlertDialogHeader>
            <AlertDialogTitle>{limiteErro?.code === 'inadimplente' ? 'Pagamento em atraso' : 'Limite insuficiente'}</AlertDialogTitle>
            <AlertDialogDescription>{limiteErro?.mensagem}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2">
            {limiteErro?.em_aberto > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2.5 text-xs" data-testid="aviso-em-aberto">
                Você tem <b>{formatCurrency(limiteErro.em_aberto)}</b> em faturas em aberto. Pagar essas faturas <b>libera o seu limite</b> na hora.
              </div>
            )}
            {(limiteErro?.faturas_pendentes || []).map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-lg p-2.5" data-testid={`fatura-pendente-${f.codigo}`}>
                <div className="text-xs">
                  <div className="font-semibold text-[#3D4F5F]">{f.codigo} · {formatCurrency(f.total)}</div>
                  <div className="text-gray-500">Vence {formatDate(f.vencimento)} · {f.status}</div>
                </div>
                {f.pix_cobranca_id ? (
                  <button onClick={() => window.open(`/pagar/${f.pix_cobranca_id}`, '_blank')} data-testid={`btn-pix-fatura-${f.codigo}`}
                    className="flex items-center gap-1.5 bg-[#2B7A3D] hover:bg-[#236632] text-white text-xs font-semibold px-3 py-2 rounded-lg shrink-0">
                    <QrCode size={14} /> Pagar PIX
                  </button>
                ) : (
                  <Link to="/parceiro/faturas" className="text-xs text-[#2B7A3D] font-semibold shrink-0">Ver fatura</Link>
                )}
              </div>
            ))}
            {limiteErro?.em_aberto > 0 && (limiteErro?.faturas_pendentes || []).length === 0 && (
              <Link to="/parceiro/faturas">
                <Button className="w-full bg-[#2B7A3D] hover:bg-[#236632]" data-testid="btn-pagar-pendentes">Pagar faturas pendentes (PIX)</Button>
              </Link>
            )}
            <Button variant={limiteErro?.em_aberto > 0 ? 'outline' : 'default'} className={limiteErro?.em_aberto > 0 ? 'w-full' : 'w-full bg-[#2B7A3D] hover:bg-[#236632]'} disabled={acao} onClick={pagarAntecipado} data-testid="btn-antecipado-dialog">
              <Wallet size={16} className="mr-1.5" /> Pagar este serviço à vista
            </Button>
            {limiteErro?.code === 'limite_insuficiente' && (
              <Link to="/parceiro/perfil?aumento=1">
                <Button variant="outline" className="w-full" data-testid="btn-solicitar-aumento-dialog">Solicitar aumento de limite</Button>
              </Link>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Fechar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Linha = ({ label, valor, destaque }) => (
  <div className="flex gap-2 border-b border-gray-50 pb-1.5">
    <span className="text-gray-500 w-32 shrink-0 text-xs pt-0.5">{label}</span>
    <span className={`${destaque ? 'font-bold text-[#2B7A3D]' : 'font-medium text-[#3D4F5F]'}`}>{valor}</span>
  </div>
);
